import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { keys } from "@/lib/queries";
import type { AuthMe, OrderItem } from "@/lib/types";
import type { ExternalCreditState } from "@/lib/externalCredit";
import type { LaunchView } from "@/lib/externalCredit";
import { buildHead } from "@/lib/seo";
import { Card } from "@/components/ui";
import { LaunchStateCard } from "./auth.launch";
import { ExternalCreditBand } from "@/components/portfolio/ExternalCreditHero";
import { OpenOrderCardBody, OrderCancelDialog, deriveOrderView } from "@/components/portfolio/OpenOrderCard";
import { FundingPendingNotice } from "@/components/trade/FundingPendingNotice";
import { AccountFundingDisabled } from "@/components/wallet/AccountFunding";

// SM-630 (SM-621 phase 15) — fixture preview of every mini-app state.
//
// No environment can provision a THIRD_PARTY_CREDIT session until the
// provider mock lands (phase 16), so the provider-specific states are
// rendered here from fixtures — the SAME components the app mounts, fed
// fixture data — so they can be reviewed and screenshotted on SIT. Phase 16
// re-captures them against the mock. Nothing here calls a server.
export const Route = createFileRoute("/mini-app-preview")({
  head: () => buildHead({ path: "/mini-app-preview", title: "Mini-app states preview (fixtures)", noindex: true }),
  component: Preview,
});

const NOW = Date.parse("2026-10-02T09:30:00Z");
const iso = (msAgo: number) => new Date(NOW - msAgo).toISOString();

const ME: AuthMe = {
  user_id: "usr_preview",
  email: "",
  role: "user",
  broker_account_id: "bac_preview_external",
  broker_id: "brk_preview",
  balance_mode: "THIRD_PARTY_CREDIT",
  session_origin: "EXTERNAL_LAUNCH",
  // SM-811: what the gateway now returns for a launched session — the
  // user's own provider id, which their chrome shows in place of a name.
  provider_user_id: "mock_user_preview01",
};

// The MINI-APP-FLOW worked example: provider 1,000; one BUY prepaid 300;
// one Deduct still unknown (25); one refused; a sale's proceeds owed back.
const STATE: ExternalCreditState = {
  broker_account_id: ME.broker_account_id!,
  currency: "GOLD",
  balance: {
    broker_account_id: ME.broker_account_id!,
    currency: "GOLD",
    available_to_trade: "675.0000",
    snapshot_balance: "700.0000",
    unresolved_deduct_reserve: "25.0000",
    other_provider_locks: "0.0000",
    prepaid_funding: "300.0000",
    pending_credit: "7.0001",
    snapshot_version: 12,
    provider_as_of: iso(40_000),
    fetched_at: iso(39_000),
    stale: false,
  },
  funding_pending: "25.0000",
  funding_pending_count: 1,
  funding_failed_count: 1,
  credit_pending: "7.0001",
  credit_pending_count: 1,
  credit_committed: "41.0000",
  committed_since: iso(86_400_000),
  row_limit: 50,
  fundings: [
    { order_id: "ord_pending_01", side: "BUY", amount: "25.0000", status: "FUNDING_PENDING", provider_transaction_id: "sm_debit_9f2c41a7d0b84e3c", created_at: iso(20_000), updated_at: iso(15_000) },
    { order_id: "ord_live_02", side: "BUY", amount: "300.0000", status: "FUNDING_COMMITTED", provider_transaction_id: "sm_debit_1b7e03c55a2d4f90", created_at: iso(600_000), updated_at: iso(599_000) },
    { order_id: "ord_sell_03", side: "SELL", amount: "0.0000", status: "FUNDING_NOT_REQUIRED", created_at: iso(900_000), updated_at: iso(900_000) },
    { order_id: "ord_refused_04", side: "BUY", amount: "5000.0000", status: "FUNDING_REJECTED", reject_code: "FUNDING_REJECTED", provider_code: "INSUFFICIENT_BALANCE", provider_transaction_id: "sm_debit_77aa10fe3c9b4d21", created_at: iso(1_800_000), updated_at: iso(1_799_000) },
    { order_id: "ord_done_05", side: "BUY", amount: "102.0000", status: "FUNDING_RETURNED", release_reason: "TERMINAL_REMAINDER", provider_transaction_id: "sm_debit_c4d2e8f6a1b04c37", created_at: iso(7_200_000), updated_at: iso(7_000_000) },
  ],
  credits: [
    { receivable_id: "crv_preview_1", kind: "TRADE_PROCEEDS", business_ref_kind: "TRADE", business_ref_id: "trd_1:ord_sell_03", amount: "7.0001", state: "DISPATCHED", pending: true, provider_transaction_id: "sm_credit_5e1f0a9c2b7d4e88", created_at: iso(60_000), updated_at: iso(50_000) },
    { receivable_id: "crv_preview_2", kind: "UNUSED_FUNDING", business_ref_kind: "ORDER", business_ref_id: "ord_done_05", amount: "41.0000", state: "COMMITTED", pending: false, provider_transaction_id: "sm_credit_0d3b6c8e4f2a4b19", created_at: iso(7_000_000), updated_at: iso(6_990_000) },
  ],
  status_poll_schedule_ms: [0, 1000, 3000, 10000, 30000, 60000],
  escalate_after_ms: 900_000,
  as_of: iso(0),
};

// A fresh launch: money at the provider, nothing in flight or returned —
// the band a launched user first sees, identical to a native one.
const FRESH: ExternalCreditState = {
  ...STATE,
  balance: { ...STATE.balance, available_to_trade: "10.0000", snapshot_balance: "10.0000", unresolved_deduct_reserve: "0.0000", prepaid_funding: "0.0000", pending_credit: "0.0000" },
  funding_pending: "0.0000",
  funding_pending_count: 0,
  funding_failed_count: 0,
  credit_pending: "0.0000",
  credit_pending_count: 0,
  credit_committed: "0.0000",
  fundings: [],
  credits: [],
};

const PENDING_ORDER: OrderItem = {
  order_id: "ord_pending_01",
  market_id: "mkt_preview",
  outcome_id: "yes",
  side: "BUY",
  price: "6200",
  quantity: "4000",
  filled_quantity: "0",
  remaining_quantity: "4000",
  status: "PENDING_FUNDING",
  tif: "GTC",
  created_at: iso(20_000),
  amount_precision: 2,
  funding_resolution: "UNKNOWN",
};

const LAUNCH_VIEWS: LaunchView[] = [
  "loading",
  "success",
  "invalid_code",
  "inactive_account",
  "provider_unavailable",
  "retry_safe_failure",
];

function Preview() {
  const qc = useQueryClient();
  useState(() => {
    for (const k of [keys.me(), keys.walletExternalCreditState()]) {
      qc.setQueryDefaults(k, { staleTime: Infinity, gcTime: Infinity, retry: false, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false, refetchInterval: false });
    }
    qc.setQueryData(keys.me(), ME);
    qc.setQueryData(keys.walletExternalCreditState(), STATE);
    return true;
  });
  const [cancelRow, setCancelRow] = useState<OrderItem | null>(null);
  const view = deriveOrderView(PENDING_ORDER);
  return (
    <div className="mx-auto min-h-dvh max-w-5xl space-y-10 bg-bg p-4 text-fg sm:p-6">
      <header>
        <h1 className="font-display text-2xl font-bold">Mini-app states (fixtures)</h1>
        <p className="mt-1 text-sm text-fg-muted">
          SM-630 / SM-654 / SM-656 / SM-666 / SM-653 — fixture-driven until the provider mock (phase 16). No server calls.
        </p>
      </header>

      <Section id="launch" title="SM-654 · Launch landing — every state">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {LAUNCH_VIEWS.map((v) => (
            <div key={v} data-preview-launch={v}>
              <p className="mb-1 font-mono text-xs text-fg-subtle">{v}</p>
              <LaunchStateCard view={v} fill={false} onEnter={() => undefined} />
            </div>
          ))}
        </div>
      </Section>

      <Section id="band" title="SM-656 / SM-666 · Provider balance, funding and Credits — the native portfolio band">
        <p className="mb-1 font-mono text-xs text-fg-subtle">fresh launch · nothing in flight (same band as native)</p>
        <ExternalCreditBand state={FRESH} positionValue={0} cost={0} pnl={0} />
        <p className="mb-1 mt-6 font-mono text-xs text-fg-subtle">every provider state non-zero · nothing under the tiles (SM-850) · no Wallet hint (SM-890)</p>
        <ExternalCreditBand state={STATE} positionValue={80} cost={67.5} pnl={12.5} />
        <p className="mb-1 mt-6 font-mono text-xs text-fg-subtle">stale snapshot · broker paused (restricted) · Credits unavailable</p>
        <ExternalCreditBand
          state={{ ...STATE, balance: { ...STATE.balance, stale: true }, credits_unavailable: true, credits: [] }}
          positionValue={80}
          cost={84}
          pnl={-4}
          restricted
        />
        <p className="mb-1 mt-6 font-mono text-xs text-fg-subtle">state not read yet · state unreadable (figures are —, never 0)</p>
        <ExternalCreditBand state={undefined} positionValue={0} cost={0} pnl={0} />
        <ExternalCreditBand state={undefined} positionValue={0} cost={0} pnl={0} failed />
      </Section>

      <Section id="orders" title="SM-666 · A funding-pending order (visible, not active)">
        <Card className="max-w-xl p-0">
          <OpenOrderCardBody view={view} onCancel={() => setCancelRow(PENDING_ORDER)} showTitle />
        </Card>
        <OrderCancelDialog row={cancelRow} onOpenChange={(o) => !o && setCancelRow(null)} onConfirm={() => setCancelRow(null)} />
      </Section>

      <Section id="trade" title="SM-630 · Submit past the client deadline — recoverable, never resubmitted">
        <div className="grid gap-4 md:grid-cols-3">
          <FundingPendingNotice orderId="ord_pending_01" cancelRequested={false} escalated={false} onCancel={async () => undefined} />
          <FundingPendingNotice orderId="ord_pending_01" cancelRequested escalated={false} onCancel={async () => undefined} />
          <FundingPendingNotice orderId="ord_pending_01" cancelRequested={false} escalated onCancel={async () => undefined} />
        </div>
      </Section>

      <Section id="restrictions" title="SM-653 · A native funding deep link in an external-credit session">
        <AccountFundingDisabled />
      </Section>
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} data-preview-section={id} className="scroll-mt-4">
      <h2 className="mb-3 text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}
